import {
  FC,
  SyntheticEvent,
  useCallback,
  useState,
  memo,
  useEffect,
  MouseEvent,
} from 'react';
import styled from 'styled-components';

import { useUserDataById } from '@redux/selectors/users';
import { TeamType } from '@typings/participant';
import { AvatarSize } from '@typings/user';
import TouchableOpacity from '@components/buttons/TouchableOpacity';
import ImageWithBackground from '@components/image/ImageWithBackground';
import ImagePlaceholder from '@components/loader/ImagePlaceholder';
import defaultAvatar from '@assets/images/avatar.png';
import defaultBackground from '@assets/images/background.png';
import { SKEW_ANGLE } from '@utils/constants';

const getAvatarWidth = (size: AvatarSize) => {
  return size === AvatarSize.Small
    ? '36px'
    : size === AvatarSize.Medium
      ? '48px'
      : '64px';
};

const getAvatarHeight = (size: AvatarSize) => {
  return size === AvatarSize.Small
    ? '42px'
    : size === AvatarSize.Medium
      ? '56px'
      : '80px';
};

const Container = styled(TouchableOpacity)<{
  $size: AvatarSize;
  $side: TeamType;
}>`
  width: ${({ $size }) => getAvatarWidth($size)};
  height: ${({ $size }) => getAvatarHeight($size)};
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  border: none;
  border-bottom: ${({ theme, $side }) =>
    $side === TeamType.Blue
      ? `2px solid ${theme.colors.blue[500]}`
      : $side === TeamType.Red
        ? `2px solid ${theme.colors.red[500]}`
        : 'none'};
  transform: skew(-${SKEW_ANGLE}deg);
  overflow: hidden;
  cursor: pointer;

  &:disabled {
    cursor: default;
  }
`;

const AvatarImage = styled.img<{ $size: AvatarSize; $isLoaded?: boolean }>`
  width: ${({ $size }) => getAvatarWidth($size)};
  height: ${({ $size }) => getAvatarWidth($size)};
  align-self: flex-end;
  z-index: 1;
  transform: skew(${SKEW_ANGLE}deg);
  scale: 1.25;
  opacity: ${({ $isLoaded }) => ($isLoaded ? 1 : 0.1)};
  transition: opacity 0.3s ease-in-out;
`;

const Background = styled.img<{ $size: AvatarSize; $isLoaded?: boolean }>`
  position: absolute;
  width: ${({ $size }) => getAvatarWidth($size)};
  height: ${({ $size }) => getAvatarHeight($size)};
  z-index: 0;
  opacity: ${({ $isLoaded }) => ($isLoaded ? 1 : 0.1)};
  transition: opacity 0.3s ease-in-out;
`;

const StyledPlaceholder = styled(ImagePlaceholder)<{ $size: AvatarSize }>`
  position: absolute;
  width: ${({ $size }) => getAvatarWidth($size)};
  height: ${({ $size }) => getAvatarHeight($size)};
`;

type Props = {
  userId?: string;
  size?: AvatarSize;
  side?: TeamType;
  disabled?: boolean;
  onClick?: () => void;
};

const Avatar: FC<Props> = ({
  userId,
  disabled,
  size = AvatarSize.Medium,
  side = TeamType.Neutral,
  onClick,
}) => {
  const user = useUserDataById(userId);
  const [avatar, setAvatar] = useState<string>(() =>
    user?.image ? user.image : defaultAvatar,
  );
  const [background, setBackground] = useState<string>(() =>
    user?.background ? user.background : defaultBackground,
  );

  const onImageLoad = useCallback((e: SyntheticEvent<HTMLImageElement>) => {
    if (e.currentTarget.naturalWidth === 0) {
      setAvatar(defaultAvatar);
    }
  }, []);

  const onImageError = useCallback(() => {
    setAvatar(defaultAvatar);
  }, []);

  const onBackgroundLoad = useCallback(
    (e: SyntheticEvent<HTMLImageElement>) => {
      if (e.currentTarget.naturalWidth === 0) {
        setBackground(defaultBackground);
      }
    },
    [],
  );

  const onBackgroundError = useCallback(() => {
    setBackground(defaultBackground);
  }, []);

  const onAvatarClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (onClick) {
        onClick();
      }
    },
    [onClick],
  );

  useEffect(() => {
    setAvatar(user?.image || defaultAvatar);
  }, [user?.image]);

  useEffect(() => {
    setBackground(user?.background || defaultBackground);
  }, [user?.background]);

  return (
    <Container
      disabled={disabled}
      $size={size}
      $side={side}
      onClick={onAvatarClick}
      data-avatar='true'
    >
      <ImageWithBackground
        image={avatar}
        background={background}
        onImageLoad={onImageLoad}
        onImageError={onImageError}
        onBackgroundLoad={onBackgroundLoad}
        onBackgroundError={onBackgroundError}
        BackgroundComponent={Background}
        ImageComponent={AvatarImage}
        PlaceholderComponent={StyledPlaceholder}
        backgroundProps={{ $size: size }}
        imageProps={{ $size: size }}
        placeholderProps={{ $size: size }}
      />
    </Container>
  );
};

export default memo(Avatar);
